import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import AnalyticsFilterBar from './AnalyticsFilterBar';
import { toQuery, useAnalyticsFilters } from './filters';
import { fmtMin, fmtNum, fmtPct } from './format';
import { alert, card, cardHead, muted } from './Overview';

// "Ranking problemów" (spec 7.9): six TOP lists with a name attached to every loss.

interface Data {
  reasons: { label: string; minutes: number; count: number }[];
  linesByDowntime: { label: string; minutes: number; count: number; availability: number | null }[];
  materialsByDowntime: { label: string; minutes: number; count: number; stopsPerProdH: number | null }[];
  materialsByPerformance: { label: string; performance: number; avgSpeed: number | null; tgtSpeed: number | null; prodMin: number }[];
  longestDowntimes: { label: string; start: string; minutes: number; reason: string | null; material: string }[];
  potentialLossByLine: { label: string; lossKg: number; utilization: number | null; loss: { downtime: number; shortStops: number; speed: number; scrap: number } }[];
  coverage: number | null;
}

const dt = (iso: string) => new Date(iso).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

const TopList: React.FC<{ title: string; sub: string; rows: { label: string; value: number; display: string; sub?: string }[]; color: string; empty: string }> = ({ title, sub, rows, color, empty }) => {
  const max = Math.max(1, ...rows.map(r => r.value));
  return (
    <div style={{ ...card, marginBottom: 0 }}>
      <div style={cardHead}><b>{title}</b><span style={muted}>{sub}</span></div>
      {rows.length === 0 ? <div style={{ ...muted, padding: 12 }}>{empty}</div> : rows.map((r, i) => (
        <div key={`${r.label}-${i}`} style={{ display: 'grid', gridTemplateColumns: '22px 1fr auto', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
          <span style={{ color: '#94a3b8', fontWeight: 700 }}>{i + 1}</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</div>
            <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden', marginTop: 3 }}><div style={{ width: `${(r.value / max) * 100}%`, height: '100%', background: color }} /></div>
          </div>
          <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}><b>{r.display}</b>{r.sub && <div style={muted}>{r.sub}</div>}</div>
        </div>
      ))}
    </div>
  );
};

const Ranking: React.FC = () => {
  const { t } = useLanguage();
  const [filters, setFilters] = useAnalyticsFilters();
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const { compare, ...rest } = toQuery(filters);
    api.get('/analytics/ranking', { params: rest })
      .then(res => { if (!cancelled) { setData(res.data); setError(null); } })
      .catch(err => { if (!cancelled) setError(err?.response?.data?.message ?? t('analytics.loadError')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters, t]);

  const reasonLabel = (r: string | null) => (r === 'no_speed' || r === 'filament_lost' ? t(`analytics.reason_${r}`) : r === 'unknown' || r === null ? t('downtime.reasonUnknown') : r);
  const empty = t('ranking.empty');

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b' }}>🏆 {t('ranking.title')}</h1>
        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: 14 }}>{t('ranking.subtitle')}</p>
      </div>
      <AnalyticsFilterBar filters={filters} onChange={setFilters} showCompare={false} />
      {error && <div style={alert}>{error}</div>}
      {loading && !data && <div style={{ color: '#94a3b8', padding: 32, textAlign: 'center' }}>{t('common.loading')}</div>}
      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16, opacity: loading ? 0.6 : 1 }}>
          <TopList title={t('ranking.reasons')} sub={t('ranking.reasonsSub')} color="#6b7280" empty={empty} rows={data.reasons.map(r => ({ label: reasonLabel(r.label), value: r.minutes, display: fmtMin(r.minutes), sub: `${r.count}×` }))} />
          <TopList title={t('ranking.lines')} sub={t('ranking.linesSub')} color="#6b7280" empty={empty} rows={data.linesByDowntime.map(r => ({ label: r.label, value: r.minutes, display: fmtMin(r.minutes), sub: `${r.count}× · Avail. ${fmtPct(r.availability)}` }))} />
          <TopList title={t('ranking.materialsDown')} sub={t('ranking.materialsDownSub')} color="#e8590c" empty={empty} rows={data.materialsByDowntime.map(r => ({ label: r.label, value: r.minutes, display: fmtMin(r.minutes), sub: `${r.count}× · ${fmtNum(r.stopsPerProdH, 2)}/h` }))} />
          <TopList title={t('ranking.materialsPerf')} sub={t('ranking.materialsPerfSub')} color="#d97706" empty={t('ranking.emptyPerf')} rows={data.materialsByPerformance.map(r => ({ label: r.label, value: 1 - r.performance, display: fmtPct(r.performance), sub: `${fmtNum(r.avgSpeed, 1)} / ${fmtNum(r.tgtSpeed, 0)} m/min · ${fmtMin(r.prodMin)}` }))} />
          <TopList title={t('ranking.longest')} sub={t('ranking.longestSub')} color="#6b7280" empty={empty} rows={data.longestDowntimes.map(r => ({ label: `${r.label} · ${dt(r.start)}`, value: r.minutes, display: fmtMin(r.minutes), sub: `${reasonLabel(r.reason)} · ${r.material}` }))} />
          <TopList title={t('ranking.potential')} sub={data.coverage !== null && data.coverage < 0.999 ? t('ranking.potentialSubCov', { pct: fmtPct(data.coverage, 0) }) : t('ranking.potentialSub')} color="#667eea" empty={t('ranking.emptyPotential')} rows={data.potentialLossByLine.map(r => ({ label: r.label, value: r.lossKg, display: `${fmtNum(r.lossKg, 0)} kg`, sub: `${t('capacity.wDown')} ${fmtNum(r.loss.downtime + r.loss.shortStops, 0)} · ${t('capacity.wSpeed')} ${fmtNum(r.loss.speed, 0)} · ${t('capacity.wScrap')} ${fmtNum(r.loss.scrap, 0)} · ${fmtPct(r.utilization, 0)}` }))} />
        </div>
      )}
    </div>
  );
};

export default Ranking;
